import { ATTENDEE_EMAIL, SPEAKER_EMAIL, SPONSOR_EMAIL } from '@utils/constants';
import {
  AccountPortfolio,
  Portfolio,
  PublicAttendee,
  PublicEventDetailsResponse,
  ResponsePaginationType,
} from '@hubs101/booking-api-skd-client/lib/types';
import { Document, Image, Link, Page, Text, View } from '@react-pdf/renderer';
import { useCallback, useMemo } from 'react';

import { DefaultTheme } from 'styled-components';
import { APP_HOST } from '@utils/apiHooks';
import Style from '@react-pdf/types';
import dayjs from 'dayjs';
import { eventTimeFormat } from '@utils/time';
import { getImageSrc } from '@utils/fn';
import { styles } from './styles';
import { useTranslation } from 'react-i18next';
import TitlePage from './AttendeeListTitlePage';
import mailIcon from '@assets/icons/mail_icon.png';
import mailIconBlack from '@assets/icons/mail_icon_black.png';
import { resolveImageColor } from '@composites/PDF/BusinessPartnerPdfContent';

interface AttendeeListProps {
  event: PublicEventDetailsResponse;
  theme: DefaultTheme;
  account: AccountPortfolio | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  attendees: PublicAttendee[] | undefined;
  logoUrl?: string;
  pastMode?: boolean;
}

const AttendeeList = ({
  event,
  theme,
  attendees,
  portfolios,
  logoUrl,
  pastMode = false,
}: AttendeeListProps) => {
  const { t } = useTranslation();
  const eventHomePageUrl = `${APP_HOST}/events/${event?.slug}`;
  const portfolioUrl = `${APP_HOST}${t('mappable-company-routes.our-product-portfolio-url')}/${event?.portfolio?.slug}`;
  const location = useMemo(
    () =>
      [event?.location?.name, event?.location?.marker?.city]
        ?.filter(Boolean)
        .join(', ') ?? '',
    [event?.location?.marker?.city, event?.location?.name]
  );

  const currentPortfolio = portfolios?.data?.find(
    p => p.slug === event?.portfolio?.slug
  );
  const portfolioDesign = (currentPortfolio?.design ?? {}) as any;
  const seriesLogoNeg = portfolioDesign?.logoNegImagePng
    ? getImageSrc(portfolioDesign.logoNegImagePng)
    : undefined;
  const seriesLogoPos = portfolioDesign?.logoPosImagePng
    ? getImageSrc(portfolioDesign.logoPosImagePng)
    : undefined;

  const eventDates = useMemo(
    () =>
      eventTimeFormat(
        event?.start_date,
        event?.end_date,
        event?.language,
        event?.timezone
      ),
    [event?.end_date, event?.language, event?.start_date, event?.timezone]
  );

  const Header = useCallback(() => {
    return (
      <View style={styles.header} fixed>
        {event?.images?.logoTransparent?.length > 0 && (
          <Link src={eventHomePageUrl} style={{ textDecoration: 'none' }}>
            <Image
              style={styles.logo}
              src={getImageSrc(event?.images?.logoTransparent)}
            />
          </Link>
        )}
        <Text style={{ ...styles.title, color: event?.design?.themeColor }}>
          {event?.name}
        </Text>
        <View style={styles.details}>
          <Text
            style={{ ...styles.location, color: theme.color.mobilityGrey3 }}
          >
            {location}
          </Text>
          <Text
            style={{ ...styles.location, color: theme.color.mobilityGrey3 }}
          >
            {eventDates}
          </Text>
        </View>
      </View>
    );
  }, [
    event?.design?.themeColor,
    event?.images?.logoTransparent,
    event?.name,
    eventDates,
    location,
    theme.color.mobilityGrey3,
  ]);

  const Footer = useCallback(
    ({
      leftLightMode,
      rightLightMode,
      noPadding = false,
      fontSize = 14,
    }: {
      leftLightMode?: boolean;
      rightLightMode?: boolean;
      noPadding?: boolean;
      fontSize?: number;
    }) => {
      return (
        <View
          style={{
            ...styles.footerTitle,
            backgroundColor:
              leftLightMode === rightLightMode && leftLightMode === false
                ? event?.design?.themeColor
                : 'transparent',
            paddingRight: 15,
            width: '96.5%',
            marginRight: 15,
            marginLeft:
              leftLightMode === rightLightMode && leftLightMode === false
                ? 15
                : 0,
          }}
          fixed
        >
          <View
            style={{
              ...styles.footerColumn,
              backgroundColor: leftLightMode
                ? 'white'
                : event?.design?.themeColor,
              borderTopLeftRadius: leftLightMode === rightLightMode ? '8px' : 0,
              color: leftLightMode ? '#27343d' : 'white',
              alignItems: 'flex-start',
              paddingLeft: noPadding
                ? 0
                : leftLightMode === rightLightMode && leftLightMode === false
                  ? 20
                  : 23,
            }}
          >
            <Link src={portfolioUrl} style={{ textDecoration: 'none' }}>
              <Image
                style={styles.logo}
                src={resolveImageColor(
                  leftLightMode
                    ? (seriesLogoPos ?? seriesLogoNeg ?? '')
                    : (seriesLogoNeg ?? seriesLogoPos ?? ''),
                  'white'
                )}
              />
            </Link>
          </View>

          <View
            style={{
              display: 'flex',
              width: '45.6875rem',
              padding: 2,
              height: '2.5rem',
              marginTop: 8,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Link
              style={{
                backgroundColor: leftLightMode
                  ? event?.design?.themeColor
                  : 'white',
                color: leftLightMode ? 'white' : event?.design?.themeColor,
                paddingHorizontal: 20,
                paddingVertical: 6,
                borderRadius: 4,
                fontSize: 11,
                textDecoration: 'none',
              }}
              src={`${eventHomePageUrl}/partnering-packages`}
            >
              {`${t('book-now')} & ${t('become-a-partner')}`}
            </Link>
          </View>

          <View
            style={{
              ...styles.footerColumn,
              backgroundColor: rightLightMode
                ? 'white'
                : event?.design?.themeColor,
              borderTopRightRadius:
                leftLightMode === rightLightMode ? '8px' : 0,
              color: rightLightMode ? '#27343d' : 'white',
              alignItems: 'flex-end',
              marginRight: 20,
              paddingRight:
                leftLightMode === rightLightMode && leftLightMode === false
                  ? 10
                  : 53,
            }}
          >
            <Link
              src={`${APP_HOST}/events/${event?.slug}`}
              style={{ textDecoration: 'none' }}
            >
              <Image
                style={styles.logo}
                src={resolveImageColor(
                  getImageSrc(event?.images?.logoTransparent),
                  'white'
                )}
              />
            </Link>
          </View>
        </View>
      );
    },
    [event?.design?.themeColor, portfolioUrl, logoUrl]
  );
  const headers = useMemo(
    () => [
      { key: t('company'), width: '30%', paddingLeft: 30 },
      { key: t('job-title'), width: '20%', paddingLeft: 0 },
      { key: t('job-level'), width: '26%', paddingLeft: 0 },
      { key: t('industry'), width: '12%', paddingLeft: 0 },
      { key: t('country'), width: '12%', paddingLeft: 0 },
    ],
    [t]
  );

  const body = useMemo(() => {
    return attendees?.map(attendee => [
      {
        value: attendee.company?.name,
        width: '30%',
        paddingLeft: 30,
        fontWeight: 'bold' as Style.FontWeight,
        fontFamily: 'Lato',
      },
      {
        value: attendee.job?.title,
        width: '20%',
        paddingLeft: 0,
        fontWeight: 'normal' as Style.FontWeight,
        fontFamily: 'Lato',
      },
      {
        value: t(`job-levels.${String(attendee.job?.level)}`),
        width: '26%',
        paddingLeft: 0,
        fontWeight: 'normal' as Style.FontWeight,
        fontFamily: 'Lato',
      },
      {
        value: t(`industries.${String(attendee.company?.industry)}`),
        width: '12%',
        paddingLeft: 0,
        fontWeight: 'normal' as Style.FontWeight,
        fontFamily: 'Lato',
      },
      {
        value: t(`countries.${String(attendee.address?.country)}`),
        width: '12%',
        paddingLeft: 0,
        fontWeight: 'normal' as Style.FontWeight,
        fontFamily: 'Lato',
      },
    ]);
  }, [attendees, t]);

  const footerButtons = useMemo(
    () => [
      {
        title: t('to-sponsor').toUpperCase(),
        email: SPONSOR_EMAIL,
      },
      {
        title: t('to-attend').toUpperCase(),
        email: ATTENDEE_EMAIL,
      },
      {
        title: t('to-speak').toUpperCase(),
        email: SPEAKER_EMAIL,
      },
    ],
    [t]
  );

  const TitleFooter = useCallback(
    ({
      leftLightMode,
      rightLightMode,
      noPadding = false,
      fontSize = 14,
    }: {
      leftLightMode?: boolean;
      rightLightMode?: boolean;
      noPadding?: boolean;
      fontSize?: number;
    }) => {
      return (
        <View
          style={{
            ...styles.footerTitle,
            backgroundColor:
              leftLightMode === rightLightMode && leftLightMode === false
                ? event?.design?.themeColor
                : 'transparent',
            width:
              leftLightMode === rightLightMode && leftLightMode === false
                ? '98%'
                : '100%',
            marginLeft:
              leftLightMode === rightLightMode && leftLightMode === false
                ? 15
                : 0,
          }}
          fixed
        >
          <View
            style={{
              ...styles.footerColumn,
              backgroundColor: leftLightMode
                ? 'white'
                : event?.design?.themeColor,
              borderTopLeftRadius: leftLightMode === rightLightMode ? '8px' : 0,
              color: leftLightMode ? '#27343d' : 'white',
              alignItems: 'flex-start',
              paddingLeft: noPadding
                ? 0
                : leftLightMode === rightLightMode && leftLightMode === false
                  ? 10
                  : 23,
            }}
          >
            <Link src={portfolioUrl} style={{ textDecoration: 'none' }}>
              {(seriesLogoNeg ?? seriesLogoPos) ? (
                <Image
                  style={styles.logo}
                  src={resolveImageColor(
                    leftLightMode
                      ? (seriesLogoPos ?? seriesLogoNeg ?? '')
                      : (seriesLogoNeg ?? seriesLogoPos ?? ''),
                    'white'
                  )}
                />
              ) : null}
            </Link>
          </View>

          <View
            style={{
              ...styles.footerColumn,
              backgroundColor: rightLightMode
                ? 'white'
                : event?.design?.themeColor,
              color: rightLightMode ? '#27343d' : 'white',
            }}
          >
            <View
              style={{
                ...styles.footerMiddleItem,
                backgroundColor: rightLightMode
                  ? 'white'
                  : event?.design?.themeColor,
                color: rightLightMode ? '#27343d' : 'white',
                fontSize: fontSize,
              }}
            >
              <Image
                src={rightLightMode ? mailIconBlack : mailIcon}
                style={styles.icon}
              />
              <Link
                href={`mailto:${SPONSOR_EMAIL}`}
                style={{
                  color: rightLightMode ? '#27343d' : 'white',
                  textDecoration: 'none',
                }}
              >
                {SPONSOR_EMAIL}
              </Link>
            </View>
          </View>

          <View
            style={{
              ...styles.footerColumn,
              backgroundColor: rightLightMode
                ? 'white'
                : event?.design?.themeColor,
              borderTopRightRadius:
                leftLightMode === rightLightMode ? '8px' : 0,
              color: rightLightMode ? '#27343d' : 'white',
              alignItems: 'flex-end',
              paddingRight:
                leftLightMode === rightLightMode && leftLightMode === false
                  ? 10
                  : 53,
            }}
          >
            <Link
              src={`${APP_HOST}/events/${event?.slug}`}
              style={{ textDecoration: 'none' }}
            >
              <Image
                style={styles.logo}
                src={resolveImageColor(
                  getImageSrc(event?.images?.logoTransparent),
                  'white'
                )}
              />
            </Link>
          </View>
        </View>
      );
    },
    [event?.design?.themeColor, t]
  );

  return (
    <Document>
      <TitlePage
        event={event}
        footer={
          <TitleFooter
            leftLightMode={false}
            rightLightMode={false}
            fontSize={8}
          />
        }
      />
      <Page size="A4" orientation="landscape" style={{ paddingBottom: 101 }}>
        <Header />

        <View style={styles.content}>
          <Text
            style={{ ...styles.tableTitle, color: theme.color.mobilityGrey3 }}
          >
            {pastMode
              ? t('past-event-attendee-list')
              : `${t('attendee-list')} ${dayjs(event?.start_date).format('YYYY')}`}
          </Text>
          <View style={styles.tableRow} fixed>
            {headers.map(header => (
              <Text
                key={header.key}
                style={[
                  styles.tableHeader,
                  {
                    width: header.width,
                    paddingLeft: header.paddingLeft,
                    borderColor: event?.design?.themeColor,
                  },
                ]}
              >
                {header.key}
              </Text>
            ))}
          </View>
          {body?.map((attendee, i) => {
            const backgroundColor = '#ffffff';
            return (
              <View
                style={{
                  ...styles.tableRow,
                  borderColor: event?.design?.themeColor,
                }}
                key={i}
              >
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: attendee?.[0].width,
                      fontWeight: attendee?.[0].fontWeight,
                      fontFamily: attendee?.[0].fontFamily,
                      paddingLeft: attendee?.[0].paddingLeft,
                      backgroundColor,
                    },
                  ]}
                >
                  {attendee?.[0].value}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: attendee?.[1].width,
                      fontWeight: attendee?.[1].fontWeight,
                      paddingLeft: attendee?.[1].paddingLeft,
                      backgroundColor,
                    },
                  ]}
                >
                  {attendee?.[1].value}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: attendee?.[2].width,
                      fontWeight: attendee?.[2].fontWeight,
                      paddingLeft: attendee?.[2].paddingLeft,
                      backgroundColor,
                    },
                  ]}
                >
                  {attendee?.[2].value}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: attendee?.[3].width,
                      fontWeight: attendee?.[3].fontWeight,
                      paddingLeft: attendee?.[3].paddingLeft,
                      backgroundColor,
                    },
                  ]}
                >
                  {attendee?.[3].value}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: attendee?.[4].width,
                      fontWeight: attendee?.[4].fontWeight,
                      paddingLeft: attendee?.[4].paddingLeft,
                      backgroundColor,
                    },
                  ]}
                >
                  {attendee?.[4].value}
                </Text>
              </View>
            );
          })}
        </View>
        <View style={styles.footerButtonsWrapper} wrap={false}>
          <Text style={styles.buttonsWrapperText}>{t('get-in-touch')}</Text>
          <View style={styles.buttonsWrapper}>
            {footerButtons.map(({ title, email }) => (
              <View key={title} style={styles.footerButton}>
                <Text style={styles.buttonTitleText}>{title}</Text>
                <Link style={styles.buttonLinkText} src={`mailto:${email}`}>
                  {email}
                </Link>
              </View>
            ))}
          </View>
        </View>
        <Footer leftLightMode={false} rightLightMode={false} fontSize={14} />
      </Page>
    </Document>
  );
};

export default AttendeeList;
